import React from 'react';
import ReactDOM from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider, createBrowserRouter } from 'react-router-dom';

import { ConfirmProvider } from './components/ConfirmDialog';
import { AuthProvider } from './features/auth/AuthProvider';
import { I18nProvider } from './i18n';
import { ThemeProvider } from './lib/theme';
import { ApiError, wakeBackend } from './lib/api';
import { routes } from './routes';
import './styles/index.css';

// Configures Amplify as a side effect, before any component can ask for a session.
import './features/auth/config';

// Start the database waking now; by the time someone has signed in it is usually up.
wakeBackend();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Fresh rather than fast-and-stale: come back to the tab and it re-reads. The
      // request layer already waits out a sleeping database, so no blanket retries
      // here — a 403 or a 404 retried three times is just a slower error.
      staleTime: 15_000,
      refetchOnWindowFocus: true,
      retry: (count, error) =>
        count < 1 && !(error instanceof ApiError && error.status >= 400 && error.status < 500),
    },
  },
});

const router = createBrowserRouter(routes);

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <ThemeProvider>
      <I18nProvider>
        <QueryClientProvider client={queryClient}>
          {/*
            Auth sits inside the router-less providers but outside the router, so the
            sign-in page gets the theme and the dictionary, and a sign-out tears down
            every route at once rather than leaving a stale page mounted.
          */}
          <ConfirmProvider>
            <AuthProvider>
              <RouterProvider router={router} />
            </AuthProvider>
          </ConfirmProvider>
        </QueryClientProvider>
      </I18nProvider>
    </ThemeProvider>
  </React.StrictMode>,
);
